import React, { useState } from "react";
import {
  generatePath,
  Link,
  Outlet,
  NavLink,
  NavLinkProps,
  createSearchParams,
} from "react-router-dom";
import HotspringLogo from "@/assets/logo.svg";
import HotspringLogoBlack from "@/assets/logo-black.png";
import DashboardIcon from "@/assets/dashboard.svg";
import PendingIcon from "@/assets/pending.svg";
import AssignedIcon from "@/assets/assigned.svg";
import CompletedIcon from "@/assets/completed.svg";
import InvoicedIcon from "@/assets/invoiced.svg";
import HelpIcon from "@/assets/help.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import EnableAI from "@/components/EnableAI/EnableAI";

import {
  ASSIGNED_URL,
  COMPLETED_URL,
  DASHBOARD_URL,
  HOME_URL,
  INVOICED_URL,
  PENDING_URL,
  PROJECT_COMPLETED_URL,
  PROJECT_DASHBOARD_URL,
  PROJECT_PENDING_URL,
  PROJECT_INVOICES_URL,
  PROJECT_ASSIGNED_URL,
  USER_ASSIGNED_URL,
  USER_PROJECT_ASSIGNED_URL,
} from "@/helpers/url.ts";
import { HELP_URL } from "@/env";
import { Theme } from "@/contexts/ThemeContext.tsx";
import { cn } from "@/lib/utils";
import ProfileDropdown from "@/components/ProfileDropdown/ProfileDropdown.tsx";
import { useUserContext } from "@/contexts/UserContextProvider";
import { useTheme } from "@/contexts/ThemeContext.tsx";
import { CompanyRole } from "@/__generated__/graphql";
import { PendingStatusOptions } from "@/pages/Pending/Filters/PendingFilters";
import SettingsSheet from "@/components/SettingsSidepanel/SettingsSheet.tsx";
import AnnouncementBanner from "@/components/common/AnnouncementBanner";
import StaleWarningBanner from "@/components/common/StaleWarningBanner";
import { PageViewModes } from "@/pages/Pending/pending.enum";

export default function MainLayout() {
  const { theme } = useTheme();
  const { currentUser, currentProject, role, systemAnnouncements } =
    useUserContext();
  const [showSettings, setShowSettings] = useState(false);

  const getBackgroundClassName = () => {
    if (currentUser?.hasSubcontracting) {
      if (role === CompanyRole.Client) {
        return "bg-[linear-gradient(85.53deg,_#DE4F4F_31.79%,_rgba(216,194,82,0.5)_99.57%)]";
      } else {
        return "bg-[linear-gradient(85.53deg,_#52C6D8_31.79%,_rgba(82,_198,_216,_0.5)_99.57%)]";
      }
    } else if (role === CompanyRole.Client) {
      return "bg-[linear-gradient(85.53deg,_#52C6D8_31.79%,_rgba(82,_198,_216,_0.5)_99.57%)]";
    } else if (role === CompanyRole.Vendor) {
      return "bg-[linear-gradient(85.41deg,_#9747FF_55.24%,_rgba(151,_71,_255,_0.5)_119.5%)]";
    }
    return "bg-[linear-gradient(90deg,_#D64FB9_0%,_rgba(214,_79,_185,_0.8)_100%)]";
  };

  const pendingUrl = (
    currentProject
      ? generatePath(PROJECT_PENDING_URL, {
          projectId: currentProject.id,
          tab: PageViewModes.BidPackages,
        })
      : PENDING_URL.replace(":tab", PageViewModes.BidPackages)
  ).concat(
    role === CompanyRole.Vendor
      ? `?${createSearchParams({
          status: PendingStatusOptions.PENDING_YOUR_BID.option,
        })}`
      : "",
  );

  const isArtist = !!currentUser?.canBeAssignedTasks;

  return (
    <div className="flex flex-col h-screen bg-primary text-primary-foreground">
      {systemAnnouncements.length > 0 && (
        <AnnouncementBanner announcements={systemAnnouncements} />
      )}
      <StaleWarningBanner />
      <nav className="flex items-center justify-between p-4 zoom">
        <Link to={HOME_URL}>
          <img
            src={theme === Theme.Dark ? HotspringLogo : HotspringLogoBlack}
            alt="Logo"
            className="w-[150px] h-[40px] object-contain"
          />
        </Link>
        <div className="flex flex-row gap-4 text-sm text-secondary-foreground">
          {isArtist ? (
            <>
              <HeaderNavLink
                to={
                  currentProject
                    ? generatePath(USER_PROJECT_ASSIGNED_URL, {
                        project_id: currentProject.id,
                      })
                    : USER_ASSIGNED_URL
                }
                icon={AssignedIcon}
              >
                Assigned
              </HeaderNavLink>
            </>
          ) : (
            <>
              <HeaderNavLink
                to={
                  currentProject
                    ? generatePath(PROJECT_DASHBOARD_URL, {
                        projectId: currentProject.id,
                      })
                    : DASHBOARD_URL
                }
                icon={DashboardIcon}
              >
                Dashboard
              </HeaderNavLink>
              <HeaderNavLink to={pendingUrl} icon={PendingIcon}>
                Pending
              </HeaderNavLink>
              <HeaderNavLink
                to={
                  currentProject
                    ? generatePath(PROJECT_ASSIGNED_URL, {
                        project_id: currentProject.id,
                      })
                    : ASSIGNED_URL
                }
                icon={AssignedIcon}
              >
                Assigned
              </HeaderNavLink>
              <HeaderNavLink
                to={
                  currentProject
                    ? generatePath(PROJECT_COMPLETED_URL, {
                        project_id: currentProject.id,
                      })
                    : COMPLETED_URL
                }
                icon={CompletedIcon}
              >
                Completed
              </HeaderNavLink>
              <HeaderNavLink
                to={
                  currentProject
                    ? generatePath(PROJECT_INVOICES_URL, {
                        project_id: currentProject.id,
                      })
                    : INVOICED_URL
                }
                icon={InvoicedIcon}
              >
                Invoiced
              </HeaderNavLink>
            </>
          )}
        </div>
        <div className="flex flex-row gap-3 items-center">
          <EnableAI />

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={HELP_URL}
                  target="_blank"
                  className="p-[8px] rounded-[8px] border border-accent-foreground hover:bg-accent shadow-[0px_1px_2px_0px_#1018280A]"
                >
                  <img
                    src={HelpIcon}
                    alt="help"
                    className="invert dark:invert-0 h-4 w-4"
                  />
                </a>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="border">
                Support
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              className="focus:outline-none focus:ring-0"
              asChild
            >
              <div
                className={cn(
                  "flex rounded-[5px] items-center space-x-3 cursor-pointer",
                  getBackgroundClassName(),
                )}
              >
                <span className="text-sm font-medium text-white ml-[8px] my-[8px]">
                  {currentUser?.hasSubcontracting && role === CompanyRole.Client
                    ? "Flashteam"
                    : currentUser?.office?.company.profile.companyName}
                </span>
                {currentUser?.profilePictureUrl ? (
                  <img
                    src={currentUser?.profilePictureUrl}
                    alt="Profile"
                    className="w-[34px] h-[34px] rounded-[5px]"
                  />
                ) : (
                  <div className="w-[32px] h-[32px] rounded-[5px] border border-white bg-transparent m-[2px]">
                    <span className="flex items-center justify-center w-full h-full text-sm font-medium text-white">
                      {currentUser?.firstName?.charAt(0).toUpperCase()}
                      {currentUser?.lastName?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="bottom"
              align="end"
              className="p-0 ml-2 rounded-[4px] border border-accent-foreground"
            >
              <ProfileDropdown setShowSettings={setShowSettings} />
            </DropdownMenuContent>
          </DropdownMenu>
          <SettingsSheet
            open={showSettings}
            onClose={() => setShowSettings(false)}
          />
        </div>
      </nav>

      <main className="flex-1 overflow-auto rounded-xl m-2 mt-0 bg-background zoom relative">
        <Outlet />
      </main>
    </div>
  );
}

interface HeaderNavLinkProps extends NavLinkProps {
  icon: string;
  children: React.ReactNode;
  className?: string;
}

function HeaderNavLink({
  icon,
  children,
  className = "",
  ...linkProps
}: HeaderNavLinkProps) {
  return (
    <NavLink
      {...linkProps}
      className={({ isActive }) =>
        cn(
          "group flex flex-row gap-1.5 rounded-[6px] px-[12px] py-[8px] transition-colors hover:bg-accent-foreground hover:text-black dark:hover:text-white",
          isActive && "bg-accent-foreground text-black dark:text-white",
          className,
        )
      }
    >
      {({ isActive }) => (
        <>
          <img
            className={cn(
              "transition invert group-hover:invert-0 group-hover:brightness-1",
              "dark:invert-0 dark:group-hover:invert dark:group-hover:brightness-0",
              isActive && "invert dark:invert dark:brightness-0",
            )}
            src={icon}
            alt="icon"
          />
          {children}
        </>
      )}
    </NavLink>
  );
}
